/*
 * @Author: Yun 
 * @Date: 2025-09-24 13:53:51 
 * @Last Modified by: Yun
 * @Last Modified time: 2025-09-25 17:51:14
 */
import React, { useEffect, useState, useRef } from 'react';
import ResourceLoader from '@utils/resourceLoader';
import { preloadAssets } from '@configs/preloadAssets';
import { content } from '@configs/content';
import styles from './index.module.scss';

interface LoaderProps {
  onLoadComplete: () => void;
  debugMode?: boolean;
}

const Loader: React.FC<LoaderProps> = ({ 
  onLoadComplete, 
  debugMode = false 
}) => {
  const [progress, setProgress] = useState(0);
  const loaderRef = useRef<ResourceLoader | null>(null);
  const [isExiting, setIsExiting] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);

  useEffect(() => {
    loaderRef.current = new ResourceLoader({
      resources: preloadAssets,
      debugMode,
      onUpdate: (percentage: number) => {
        setProgress(percentage);
      },
      onElementLoaded: () => {
        // 资源加载完成
      },
      onComplete: () => {
        // 1. 设置进度为 100%
        setProgress(100);
        
        // 2. 立即开始渲染主内容（在后台，Loader 还在前面遮挡）
        onLoadComplete();
        
        // 3. 停留 1 秒展示完成状态
        setTimeout(() => {
          // 4. 开始退场动画
          setIsExiting(true);
          
          // 5. 退场动画 1 秒后完全隐藏
          setTimeout(() => {
            setShouldHide(true);
          }, 1000);
        }, 1000);
      },
      onError: (resource, error) => {
        console.warn('Resource load failed:', resource.url, error);
      }
    });

    loaderRef.current.init().catch(error => {
      console.error('Resource loader initialization failed:', error);
      onLoadComplete();
      setIsExiting(true);
      setTimeout(() => {
        setShouldHide(true);
      }, 1000);
    });

    return () => {
      if (loaderRef.current) {
        loaderRef.current = null;
      }
    };
  }, [onLoadComplete, debugMode]); 

  // 完全隐藏后不渲染
  if (shouldHide) {
    return null;
  }

  return (
    <div className={`${styles.loader} ${progress === 100 ? styles.complete : ''} ${isExiting ? styles.exiting : ''}`}>
      {/* 背景渐变 */}
      <div className={styles.background} />
      
      {/* 主内容 */}
      <div className={styles.content}>
        {/* 进度条 */}
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div className={styles.progressTrack} />
            <div 
              className={styles.progressFill} 
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className={styles.percentage}>{progress}%</div>
        </div>
      </div>
    </div>
  );
};

export default Loader;